import React, { useState, useEffect, useCallback } from "react";
import { Wallet, RefreshCw, Loader2, TrendingDown, TrendingUp, Minus, ShieldCheck, AlertTriangle } from "lucide-react";
import { brokerMoney, deskGet } from "../lib/api";

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const signed = (v) => `${v > 0 ? "+" : v < 0 ? "-" : ""}$${Math.abs(Number(v || 0)).toFixed(2)}`;
const tone = (v) => (v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-cyan-200/60");

const Tile = ({ label, value, sub, id, accent }) => (
  <div className="bg-[#050b18] border border-[#ffffff10] rounded-xl px-3 py-2" data-testid={`money-${id}`}>
    <p className="text-cyan-300/50 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`font-bold text-base ${accent || "text-cyan-50"}`}>{value}</p>
    {sub && <p className="text-cyan-200/40 text-[10px]">{sub}</p>}
  </div>
);

const Regime = ({ r }) => {
  const Icon = r?.trend === "down" ? TrendingDown : r?.trend === "up" ? TrendingUp : Minus;
  const cls = r?.trend === "down" ? "text-rose-300" : r?.trend === "up" ? "text-emerald-300" : "text-cyan-200/60";
  const rule = r?.trend === "down" ? "crypto: shorts only" : r?.trend === "up" ? "crypto: longs only" : "crypto: both ways";
  return (
    <p className={`text-[11px] flex items-center gap-1.5 ${cls}`} data-testid="money-regime">
      <Icon className="w-3.5 h-3.5" /> BTC trend {String(r?.trend || "neutral").toUpperCase()} · {rule} <span className="text-cyan-200/40">({r?.why})</span>
    </p>
  );
};

const LedgerRow = ({ r }) => (
  <div className="grid grid-cols-12 gap-2 text-[11px] py-1 border-b border-[#ffffff08]" data-testid="money-ledger-row">
    <span className="col-span-3 font-mono text-cyan-50 truncate">{r.symbol}</span>
    <span className="col-span-2 text-cyan-300/50 truncate">{r.venue}{r.side === "short" || r.side === "no" ? ` · ${r.side}` : ""}</span>
    <span className="col-span-2 text-right text-cyan-100">{usd(r.usd_in)}</span>
    <span className="col-span-2 text-right text-cyan-100">{usd(r.usd_now_or_out)}</span>
    <span className="col-span-1 text-right text-cyan-200/50">{usd(r.fees)}</span>
    <span className={`col-span-2 text-right font-semibold ${tone(r.net_pnl)}`}>{signed(r.net_pnl)}{r.status === "open" ? " ·open" : ""}</span>
  </div>
);

export const NexusMoney = ({ userId }) => {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [desk, setDesk] = useState(null);
  const load = useCallback(async () => {
    setBusy(true);
    try { setM(await brokerMoney(userId)); } catch (e) {} finally { setBusy(false); }
    try { setDesk(await deskGet(userId)); } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  if (!m) return <div className="rounded-2xl border border-[#ffffff14] bg-[#0a1020] p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-300/40" /></div>;
  const rec = m.reconcile || {};
  const untracked = rec.untracked || [];
  return (
    <div className="rounded-2xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3" data-testid="exec-money-panel">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm"><Wallet className="w-4 h-4 text-[#FFD700]" /> Money</p>
        <button onClick={load} data-testid="money-refresh" className="text-cyan-300/50 hover:text-cyan-100">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
      </div>
      {!m.engine_on && <p className="text-[11px] text-orange-300 flex items-center gap-1.5" data-testid="money-engine-off"><AlertTriangle className="w-3.5 h-3.5" />This is the staging copy, so it does not trade. Only the live app places and sells trades.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile id="equity" label="Trading account" value={usd(m.equity)} sub="cash + bot trades" accent="text-[#FFD700]" />
        <Tile id="cash" label="Cash" value={usd(m.cash.total)} sub={`USD ${usd(m.cash.coinbase_usd)} · USDC ${usd(m.cash.coinbase_usdc)} · Kalshi ${usd(m.cash.kalshi)}`} />
        <Tile id="in-trades" label="In trades" value={usd(m.in_trades)} sub={`${m.open_positions} open positions`} />
        <Tile id="reserve" label={`Reserve (${m.rules.reserve_pct}%)`} value={usd(m.reserve)} sub="never invested" />
        <Tile id="free" label="Free cash" value={usd(m.free)} sub="cash above the reserve" />
        <Tile id="sweep-budget" label="Next sweep can spend" value={usd(m.sweep_budget)} sub={`${m.rules.sweep_spend_pct}% of free cash, split by confidence`} accent="text-emerald-300" />
        <Tile id="futures-bp" label="Shorts buying power" value={usd(m.cash.futures_buying_power)} sub="Coinbase futures" />
        <Tile id="fees-today" label="Fees today" value={usd(m.fees_today)} sub="actually charged" />
      </div>
      <Regime r={m.regime} />
      {desk?.mm?.equity && (
        <p className="text-[11px] text-cyan-200/70" data-testid="money-mm-desk">
          Kalshi 15-min market-maker trial ({desk.mm.settings.mm_enabled ? "on" : "off"}{desk.mm.settings.mm_paused_day ? ", paused today" : ""}):
          {" "}pot {usd(desk.mm.equity.funded)} · now {usd(desk.mm.equity.cash + desk.mm.equity.positions_value)} ·
          {" "}<span className={tone(desk.mm.equity.pnl)}>{signed(desk.mm.equity.pnl)}</span> · {desk.mm.filled_orders} fills · stops at -{usd(desk.mm.settings.mm_stop_loss)}
        </p>
      )}
      <p className="text-[11px] text-cyan-200/50 flex items-center gap-1.5" data-testid="money-reconcile">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-300/70" />
        Checked against your brokers {rec.at ? String(rec.at).slice(11, 16) + " UTC" : "—"}
        {(rec.closed || []).length > 0 && ` · fixed ${rec.closed.length} stale record(s)`}
        {(rec.resized || []).length > 0 && ` · resized ${rec.resized.length}`}
      </p>
      {untracked.length > 0 && (
        <details className="text-[11px]" data-testid="money-untracked">
          <summary className="text-cyan-200/60 cursor-pointer">Other holdings the bot didn't buy and will never touch ({untracked.length})</summary>
          <div className="mt-1 space-y-0.5">{untracked.map((u, i) => <p key={i} className="text-cyan-200/50">{u.venue} · {u.symbol}{u.side ? ` ${u.side}` : ""} · {u.qty} · {u.value != null ? usd(u.value) : ""}</p>)}</div>
        </details>
      )}
      <div>
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-cyan-300/40 pb-1 border-b border-[#ffffff10]">
          <span className="col-span-3">Trade</span><span className="col-span-2">Venue</span><span className="col-span-2 text-right">$ in</span>
          <span className="col-span-2 text-right">$ now/out</span><span className="col-span-1 text-right">Fees</span><span className="col-span-2 text-right">Net</span>
        </div>
        <div className="max-h-72 overflow-y-auto">{m.ledger.length ? m.ledger.map((r) => <LedgerRow key={r.id} r={r} />) : <p className="text-cyan-200/40 text-xs py-3 text-center">No trades yet.</p>}</div>
      </div>
    </div>
  );
};
